import React, { useRef, useEffect, useState } from 'react';
import { Mui } from '@osu/react-ui';
import { VerticalTimelineStep } from '../core';
import { isNoApplicationsScenario } from '../../utils/stepDefinitions';
import NoApplicationsComponent from './NoApplicationsComponent';

/**
 * Enhanced DashboardContent Component for V2
 * Main content area that renders timeline with template-based step rendering
 */
function DashboardContent({ 
  applicationData, 
  timelineSteps = [], 
  stepNavigation = null,
  onStepAction 
}) {
  const containerRef = useRef(null);
  const stepsContainerRef = useRef(null);
  const [lineHeight, setLineHeight] = useState(0);
  // has593v2 — the icon size is now responsive (smaller on mobile), so the timeline line's start
  // point can no longer assume a fixed 24px icon center. Measure the FIRST icon's center (x/y) from
  // the DOM and drive the line's left/top from it, mirroring the existing last-icon measurement.
  const [lineStart, setLineStart] = useState({ left: 24, top: 24 });

  // Use visible steps from step navigation if available, otherwise fall back to all timeline steps
  const stepsToShow = stepNavigation?.visibleSteps || timelineSteps;

  // Measure the timeline line length from the real DOM so it always ends at the center of the
  // last active step's icon, regardless of how tall the active step's content (e.g. the expanded
  // preferences form) grows. Previously this was estimated with hardcoded per-step pixel heights,
  // which overshot and left the line dangling below the content.
  useEffect(() => {
    const measureLine = () => {
      const stepsEl = stepsContainerRef.current;
      if (!stepsEl) { return; }

      const stepEls = stepsEl.querySelectorAll('[data-step-id]');
      const containerRect = stepsEl.getBoundingClientRect();

      // First icon: anchor the line's start at its true center (x/y), so the line stays aligned
      // regardless of the responsive icon size (has593v2).
      const firstIcon = stepEls[0]?.querySelector('[data-step-icon]') || stepEls[0]?.firstElementChild;
      // On phones the icon is display:none (has593v2), so it has no layout box. Skip measuring/showing
      // the line entirely in that case — the line is also hidden via sx, but this avoids computing a
      // bogus height from zero-size rects.
      if (!firstIcon || firstIcon.getBoundingClientRect().height === 0) { setLineHeight(0); return; }
      {
        const r = firstIcon.getBoundingClientRect();
        setLineStart({
          left: Math.round(r.left + r.width / 2 - containerRect.left),
          top: Math.round(r.top + r.height / 2 - containerRect.top),
        });
      }

      // Walk backwards to the last active/completed/in-progress step (the furthest point the line
      // should reach), mirroring getLastActiveStepIndex below.
      let lastActiveEl = null;
      for (let i = stepEls.length - 1; i >= 0; i--) {
        const idx = Number(stepEls[i].getAttribute('data-step-index'));
        const step = stepsToShow[idx];
        if (step && (step.status === 'COMPLETE' || step.status === 'IN_PROGRESS' || step.isActive)) {
          lastActiveEl = stepEls[i];
          break;
        }
      }
      if (!lastActiveEl) { setLineHeight(0); return; }

      const icon = lastActiveEl.querySelector('[data-step-icon]') || lastActiveEl.firstElementChild;
      if (!icon) { setLineHeight(0); return; }

      const iconRect = icon.getBoundingClientRect();
      // Center of the last active icon, relative to the steps container top. The line starts at the
      // first icon's center (measured above), so its height is the distance between the two centers.
      const firstIconCenter = firstIcon
        ? firstIcon.getBoundingClientRect().top + firstIcon.getBoundingClientRect().height / 2 - containerRect.top
        : 24;
      const iconCenter = iconRect.top + iconRect.height / 2 - containerRect.top;
      setLineHeight(Math.max(0, Math.round(iconCenter - firstIconCenter)));
    };

    measureLine();
    window.addEventListener('resize', measureLine);
    // Re-measure after dynamic content (form expand/collapse, async programs) settles.
    const timer = setTimeout(measureLine, 150);

    return () => {
      window.removeEventListener('resize', measureLine);
      clearTimeout(timer);
    };
  }, [stepsToShow, applicationData]);

  // Show no applications component if needed
  if (isNoApplicationsScenario(applicationData)) {
    return <NoApplicationsComponent />;
  }

  // Find the last active or completed step for continuous line
  const getLastActiveStepIndex = () => {
    for (let i = stepsToShow.length - 1; i >= 0; i--) {
      const step = stepsToShow[i];
      if (step.status === 'COMPLETE' || step.status === 'IN_PROGRESS' || step.isActive) {
        return i;
      }
    }
    return -1;
  };

  const lastActiveStepIndex = getLastActiveStepIndex();
  const shouldShowTimeline = stepsToShow.length > 0 && lastActiveStepIndex >= 0 && lineHeight > 0;

  return (
    <Mui.Paper elevation={2} sx={{ p: { xs: 1.5, sm: 4 }, mb: 4 }} ref={containerRef}>
      <Mui.Box sx={{ position: "relative" }}>
        {/* Timeline Line - Continuous line through active steps */}
        {shouldShowTimeline && (
          <Mui.Box
            sx={{
              position: "absolute",
              // has593v2 — the connector line is hidden on phones (xs); the icons it connects are also
              // hidden there, so the dashboard reads as a plain stacked list on phones.
              display: { xs: "none", sm: "block" },
              // Measured from the first icon's center so the line aligns at any icon size.
              left: `${lineStart.left}px`,
              top: `${lineStart.top}px`,
              height: `${lineHeight}px`,
              width: "2px",
              backgroundColor: "divider",
              transform: "translateX(-1px)", // center the 2px line on the icon's center x
              zIndex: 0,
            }}
          />
        )}

        {/* Timeline Steps */}
        <Mui.Box ref={stepsContainerRef} sx={{ position: "relative", zIndex: 1 }}>
          {stepsToShow.length > 0 ? (
            stepsToShow.map((step, index) => (
              <VerticalTimelineStep
                key={step.id}
                step={step}
                stepIndex={index}
                isLast={index === stepsToShow.length - 1}
                onAction={onStepAction}
                applicationData={applicationData}
              />
            ))
          ) : (
            <Mui.Box sx={{ textAlign: 'center', py: 4 }}>
              <Mui.Typography variant="h6" color="text.secondary">
                No timeline steps available
              </Mui.Typography>
              <Mui.Typography variant="body2" color="text.secondary">
                Application data may be incomplete or invalid.
              </Mui.Typography>
            </Mui.Box>
          )}
        </Mui.Box>
      </Mui.Box>
    </Mui.Paper>
  );
}

export default DashboardContent;